import React, { memo, lazy, Suspense, useEffect } from "react";
import { motion } from "framer-motion";
import Header from "../Components/Header";
import truckImage from "../Assets/dashImg.png";
import { ArrowRight } from "lucide-react";
import Footer from "../Components/Footer";

// ✅ Lazy Load Sections
const KeyFeature = lazy(() => import("../Components/KeyFeature"));
const HowItwork = lazy(() => import("../Components/HowItwork"));
const Testimonial = lazy(() => import("../Components/Testimonial"));
const ManageOpration = lazy(() => import("../Components/ManageOpration"));
const FooterCta = lazy(() => import("../Components/FooterCta"));

const Home = () => {
  // ✅ Animations outside render
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5, ease: "easeOut" },
  };

  const staggerContainer = {
    animate: {
      transition: { staggerChildren: 0.1 },
    },
  };

  // ✅ Load Calendly script only once
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.async = true;

    script.onload = () => {
      console.log("✅ Calendly script loaded");
    };

    script.onerror = () => {
      console.error("❌ Failed to load Calendly script");
    };

    document.body.appendChild(script);

    const handleEvent = (e) => {
      if (e?.data?.event === "calendly.event_scheduled") {
        console.log("✅ Meeting Scheduled:", e.data);
      }
    };

    window.addEventListener("message", handleEvent);
    return () => window.removeEventListener("message", handleEvent);
  }, []);

  // ✅ Popup handler with fallback retry
  const handleBookDemo = () => {
    console.log("👆 Book Demo clicked");

    const openPopup = () => {
      if (window.Calendly?.initPopupWidget) {
        window.Calendly.initPopupWidget({
          url: "https://calendly.com/triosoft-support/30min",
        });
        console.log("✅ Calendly Popup Opened");
        return true;
      }
      return false;
    };

    // ✅ Try opening immediately
    if (openPopup()) return;

    // ✅ Retry after delay if script isn't ready
    console.warn("⚠️ Calendly not ready, retrying...");
    setTimeout(() => {
      if (!openPopup()) {
        alert("Calendly is still loading. Please try again in a moment.");
      }
    }, 500);
  };

  return (
    <main>
      <Header />

      {/* ✅ Hero Section */}
      <section
        className="relative overflow-hidden bg-[var(--page-bg)] py-10 lg:py-32"
        id="home-hero"
      >
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16 items-center">
            {/* Text */}
            <motion.div
              initial="initial"
              animate="animate"
              variants={staggerContainer}
            >
              <motion.h1
                className="font-heading text-3xl lg:text-6xl font-bold leading-tight mb-6 popingFont"
                variants={fadeInUp}
              >
                Indian Transporters ka Smart TMS Solution
              </motion.h1>

              <motion.p
                className="text-md lg:text-lg text-[--gray-text] mb-8"
                variants={fadeInUp}
              >
                Trip, LR/Bilty, Fuel, Accounts & Fleet — sab ek software me.
              </motion.p>

              <motion.div className="flex flex-wrap gap-5" variants={fadeInUp}>
                <motion.button
                  className="second_theme_btn flex items-center"
                  whileTap={{ scale: 0.95 }}
                  onClick={handleBookDemo}
                >
                  Book Demo <ArrowRight className="ml-2 h-5 w-5" />
                </motion.button>
              </motion.div>
            </motion.div>

            {/* Image */}
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <motion.img
                src={truckImage}
                alt="Maalwahak Transport Management"
                title="Maalwahak Transport Management"
                className="w-full h-auto rounded-xl"
                loading="lazy"
                decoding="async"
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 250 }}
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ✅ Lazy Sections */}
      <Suspense fallback={<div className="text-center py-20">Loading...</div>}>
        <KeyFeature />
        <HowItwork />
        <Testimonial />
        <ManageOpration />
        <FooterCta
          title="Ready to transform your transport business?"
          subtitle="Schedule a demo and see how MaalWahak can help you grow"
        />
      </Suspense>

      <Footer />
    </main>
  );
};

export default memo(Home);
